import React, {
    useCallback, useEffect, useLayoutEffect, useRef, useState
} from "react";
import './SideScroller.style.scss';
const clamp = n => Math.max(0, Math.round(n));

export default function SideScroller({
                                         children,
                                         className = "",
                                         contentClassName = "", // your scroll area class
                                         step = 160,
                                         ariaLabel = "",
                                         recalcKey // e.g. `${items.length}-${lastUpdate}`
                                     }) {
    const scrollerRef = useRef(null);
    const contentRef  = useRef(null);

    const [atStart, setAtStart] = useState(true);
    const [atEnd, setAtEnd] = useState(false);
    const [hasOverflow, setHasOverflow] = useState(false);

    const updateFlags = useCallback(() => {
        const el = scrollerRef.current;
        if (!el) return;
        const maxLeft = el.scrollWidth - el.clientWidth;
        const left = clamp(el.scrollLeft);
        setAtStart(left <= 0);
        setAtEnd(left >= maxLeft - 1);
        setHasOverflow(maxLeft > 0);
    }, []);

    useEffect(() => {
        const el = scrollerRef.current;
        if (!el) return;
        const onScroll = () => updateFlags();
        el.addEventListener("scroll", onScroll, { passive: true });
        updateFlags();
        return () => el.removeEventListener("scroll", onScroll);
    }, [updateFlags]);

    useLayoutEffect(() => {
        const scroller = scrollerRef.current;
        const content  = contentRef.current;
        if (!scroller || !content) return;
        const ro = new ResizeObserver(updateFlags);
        ro.observe(scroller);
        ro.observe(content);
        const id = requestAnimationFrame(updateFlags);
        return () => { cancelAnimationFrame(id); ro.disconnect(); };
    }, [updateFlags]);

    useEffect(() => {
        const t = setTimeout(() => requestAnimationFrame(updateFlags), 0);
        return () => clearTimeout(t);
    }, [recalcKey, updateFlags]);

    const scrollBy = delta =>
        scrollerRef.current?.scrollBy({ left: delta, behavior: "smooth" });

    const onWheel = e => {
        if (!scrollerRef.current) return;
        if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
            e.preventDefault();
            scrollerRef.current.scrollBy({ left: e.deltaY });
        }
    };

    return (
        <div className={`SideScroller ${className}`} aria-label={ariaLabel} role="group">
            {hasOverflow && !atStart && (
                <button
                    type="button"
                    className="ss-arrow ss-arrow-left"
                    onClick={() => scrollBy(-step)}
                >
                    <i className="fa-solid fa-angle-left" />
                </button>
            )}

            <div
                ref={scrollerRef}
                className={contentClassName}
                style={{ overflowX: "hidden", overflowY: "hidden" }}
                onWheel={onWheel}
            >
                <div
                    ref={contentRef}
                    className="ss-inner"
                    style={{ display: "inline-flex", gap: 0 }}
                    onTransitionEnd={updateFlags}
                >
                    {children}
                    <div style={{ width: 120, flex: "0 0 auto" }} />
                </div>
            </div>

            {hasOverflow && !atEnd && (
                <button
                    type="button"
                    className="ss-arrow ss-arrow-right"
                    onClick={() => scrollBy(step)}
                >
                    <i className="fa-solid fa-angle-right" />
                </button>
            )}
        </div>
    );
}
